import React, { useEffect, useState, useContext } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Toast } from 'react-bootstrap';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import ENDPOINT from './data/ENDPOINT';
import SecurityHeaders from './data/SecurityHeaders';
import { AuthContext } from './context/AuthContext';

const TEST = () => {
  const { user } = useContext(AuthContext);
  const [toast, setToast] = useState(false);
  const [toastData, setToastData] = useState('');
  const [details, setDetails] = useState(null);
  const [file, setFile] = useState(null);
  const [userDetails, setUserDetails] = useState({
    user_name: '',
    email: '',
    phone_number: '',
  });
  const [loading, setLoading] = useState(false);

  // Fetch payment scanner details
  const fetchPaymentDetails = async () => {
    try {
      const data = new FormData();
      const res = await axios.post(ENDPOINT + "get-scanner-details.php", data, SecurityHeaders);
      if (res.data.status === "success") {
        setDetails(res.data.data);
      }
    } catch (error) {
      setToastData('Failed to load payment details');
      setToast(true);
    }
  };

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    setUserDetails((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setToastData('Please upload only JPEG, PNG, or GIF images');
        setToast(true);
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setToastData('File selected: ' + selectedFile.name);
      setToast(true);
    }
  };

  // Upload screenshot
  const uploadScreenShots = async (e) => {
    e.preventDefault();
    if (!file) {
      setToastData('Please select a screenshot to upload');
      setToast(true);
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      for (const [key, value] of Object.entries(userDetails)) {
        data.append(key, value);
      }
    if(user)   data.append("session", user.SessionId || '');
      data.append("id_photo", file);
     

      const res = await axios.post(`${ENDPOINT}upload-payment-screen-shot.php`, data, SecurityHeaders);

      setLoading(false);
      if (res.data.status === "success") {
        setToastData('Screenshot uploaded successfully');
        setToast(true);
        setFile(null); // Reset file input
        setUserDetails({ user_name: '', email: '', phone_number: '' }); // Reset form
      } else {
        setToastData(res.data.message || 'Upload failed');
        setToast(true);
      }
    } catch (error) {
      setLoading(false);
      setToastData('Upload failed: ' + error.message);
      setToast(true);
    }
  };

  return (
    <>
      <div className="position-fixed bottom-0 start-50 translate-middle-x w-100 d-flex justify-content-center">
        <Toast
          className="bg-warning"
          onClose={() => setToast(false)}
          show={toast}
          delay={3000}
          autohide
        >
          <Toast.Header className="bg-warning text-dark">
            <strong className="me-auto">{toastData}</strong>
          </Toast.Header>
        </Toast>
      </div>

      <div className="container col-lg-8 col-md-10 col-sm-12 col-12 mx-sm-auto rounded-3 bg-white shadow-none md-shadow mt-2 mt-md-5 mb-5">
        <div className="p-0 md-p-5 d-flex justify-content-between flex-wrap mx-sm-auto">
          {details ? (
            <div className="col-lg-5 col-md-6 col-sm-12 col-12 mx-sm-auto">
              <div className="col-12 d-flex justify-content-sm-center">
                <div className="col-lg-11 col-md-9 col-sm-8 col-12">
                  <img className="w-100 p-0 ps-0" src={details.scanner_url} alt="QR Code" />
                  <div className="col-12 mt-2">
                    <div className="d-flex align-items-center">
                      <h5 className="m-0 p-0">Acc Name:</h5>
                      <p className="m-0 p-0 ms-2">{details.name}</p>
                    </div>
                    <div className="d-flex m-2 ms-0">
                      <h6>UPI-ID: {details.upi_id}</h6>
                      <CopyToClipboard
                        onCopy={() => {
                          setToastData('Copied UPI ID');
                          setToast(true);
                        }}
                        text={details.upi_id}
                      >
                        <ContentCopyIcon className="ms-3" />
                      </CopyToClipboard>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <CircularProgress />
          )}

          <div className="col-lg-7 col-md-6 col-sm-12 col-12">
            <form onSubmit={uploadScreenShots}>
              <Alert variant="filled" className="mb-3 mt-3 mt-lg-0 mt-md-0" severity="info">
                Our team will contact you within 24 hours once you have uploaded your payment screenshot.
              </Alert>
              <h4>Follow these Instructions</h4>

              <input
                placeholder="Enter your full name"
                className="form-control mb-2"
                name="user_name"
                value={userDetails.user_name}
                onChange={handleInputChange}
                type="text"
                required
              />
              <input
                placeholder="Enter your phone number"
                className="form-control mb-2"
                name="phone_number"
                value={userDetails.phone_number}
                onChange={handleInputChange}
                type="tel"
                required
              />
              <input
                placeholder="Enter your email"
                className="form-control mb-2"
                name="email"
                value={userDetails.email}
                onChange={handleInputChange}
                type="email"
                required
              />

              <input
                id="payment-button"
                className="d-none"
                onChange={handleFileChange}
                type="file"
                accept="image/jpeg,image/png,image/gif"
              />
              <div className="d-flex justify-content-start col-12">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => document.getElementById('payment-button').click()}
                  className="btn me-2 btn-primary mt-3"
                >
                  {file ? "Change Photo" : "Upload Screenshot"}
                </button>
                {file && (
                  <button type="submit" disabled={loading} className="btn btn-success mt-3">
                    {loading ? (
                      <>
                        <CircularProgress size={20} className="text-light" /> Uploading...
                      </>
                    ) : (
                      "Complete Registration"
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TEST;